import type { PayloadAction } from "@reduxjs/toolkit";
import type { BaseQueryApi } from "@reduxjs/toolkit/query/react";
import type { EditorRootState } from "@v1/store/store";
import type { Component } from "schemas/component";
import type {
  AIComponentAction,
  AIMetaEvent,
  AITriggeringFeature,
  BuildAssistantParams,
} from "schemas/generated/ai";
import type { SchemaReploShopifyProduct } from "schemas/generated/product";

import { fetchEventSource } from "@microsoft/fetch-event-source";
import { createSlice } from "@reduxjs/toolkit";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  scrollToComponentId,
  setCanvasInteractionMode,
} from "@v1/components/canvas/canvas-reducer";
import { warningToast } from "@v1/components/designSystem/alert/Toast";
import {
  handleStreamingAction,
  markStreamingUpdateFinished,
  selectDraftElement_warningThisWillRerenderOnEveryUpdate,
  setEditorMode,
  setStreamingUpdate,
} from "@v1/reducers/core-reducer";
import { getTokenFromStorage } from "@v2/features/infra/auth";
import { defaultFetchCredentials, getPublisherUrl } from "@v2/utils/env";
import { aiMetaEventSchema } from "schemas/ai";
import { errorUserFacingDetailsSchema } from "schemas/errors";
import { v4 as uuidv4 } from "uuid";

// TODO (Evan, 2024-10-15, REPL-14102): All of the AI streaming provider's local state
// should be moved to this slice.
export type AIState = {
  /* TODO (Yuxin, 2025-03-03, REPL-14102): Put everything in here, including actions. */
  lastAIResponse: {
    thoughts: string;
    reasoning: string;
    chatId: string;
  };
  lastAIActions: any[]; // Store the actions separately
  component: Component | null;
  initialBuildPrompt: string | null;
  lastReasoning: string;
};

const initialState: AIState = {
  lastAIResponse: { thoughts: "", reasoning: "", chatId: "" },
  lastAIActions: [],
  component: null,
  initialBuildPrompt: null,
  lastReasoning: "",
};

const aiSlice = createSlice({
  name: "ai",
  initialState,
  reducers: {
    handleChatbotComponent: (
      state,
      action: PayloadAction<{ component: Component }>,
    ) => {
      const { component } = action.payload;
      state.component = component;
    },
    /**
     * Store the chatbot thoughts so we can display them on the frontend
     */
    handleChatbotThoughts: (
      state,
      action: PayloadAction<{
        thoughts: string;
        reasoning: string;
        chatId: string;
      }>,
    ) => {
      state.lastAIResponse = action.payload;
    },
    /**
     * Store the AI actions separately
     */
    handleAIActions: (state, action: PayloadAction<{ action: any }>) => {
      const { action: aiAction } = action.payload;
      // Add the action to the array
      state.lastAIActions.push(aiAction);
    },
    /**
     * Reset the AI actions array (called at the start of a new generation)
     */
    resetAIActions: (state) => {
      state.lastAIActions = [];
    },
  },
});

type KeysPopulatedInQueryFn =
  | "streamingUpdateId"
  | "screenshotDependencies"
  | "model"
  | "elementId";

// Note (Evan, 2024-07-09): This function streams actions from a given url, with a given
// JSON body. We also pass the componentId (to ensure all actions have componentId defined).
// This isn't ~technically~ a BaseQueryFn, but it's pretty close - the only differences are
// that we take simpler args (i.e., we put the query args into the body before passing),
// and we don't return anything.
const aiStreamingQuery = async (
  args: {
    url: string;
    body: Omit<BuildAssistantParams, KeysPopulatedInQueryFn>;
    componentId: string;
    // Note (Evan, 2024-10-09): Optional callback that runs when the request closes
    onFinish?: () => void;
    shouldScrollToComponent?: (
      action: AIComponentAction,
      metaEvents: Array<AIMetaEvent>,
    ) => boolean;
  },
  { signal, dispatch, getState }: BaseQueryApi,
) => {
  const {
    url,
    body: _body,
    onFinish,
    shouldScrollToComponent = () => true,
  } = args;

  const draftElement = selectDraftElement_warningThisWillRerenderOnEveryUpdate(
    getState() as EditorRootState,
  );
  if (!draftElement) {
    return;
  }

  const draftElementComponent = draftElement.component;

  const streamingUpdateId = uuidv4();

  // Inject the following into the body (but allow them to also be overwritten):
  // streamingUpdateId,
  // screenshotDependencies,
  // model
  // @ts-ignore (Patrick, 2025-02-14): This is a temporary fix to allow the body to be passed in as a generic type
  const body: BuildAssistantParams = {
    streamingUpdateId,
    model: "claude-sonnet-4-5-20250929",
    ..._body,
  };

  // Note (Evan, 2024-06-04): Return to normal editing when aborted
  const abortHandler = () => {
    dispatch(setStreamingUpdate(null));
    dispatch(setEditorMode("edit"));
    dispatch(setCanvasInteractionMode("edit"));
  };

  signal.addEventListener("abort", abortHandler);

  const metaEvents: Array<AIMetaEvent> = [];
  await fetchEventSource(url, {
    method: "POST",
    headers: {
      authorization: `Token ${getTokenFromStorage()}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
    signal,
    openWhenHidden: true,
    onopen: async () => {
      // Reset the actions array at the start of a new generation
      dispatch(resetAIActions());

      dispatch(
        setStreamingUpdate({
          id: streamingUpdateId,
          draftElementComponent,
          isStreaming: true,
          componentIdsSelected:
            "componentsToGenerate" in body
              ? body.componentsToGenerate?.map(
                  (component: any) => component.component.id,
                )
              : [],
          actions: [],
          originalStyles: {},
          textMap: {},
          repaintKey: 0,
        }),
      );
    },

    onmessage: (event) => {
      // Note (Evan, 2024-06-11): If an error event comes in, throw an error
      // so the onerror handler kicks in
      if (event.event === "error") {
        throw JSON.parse(event.data);
      }
      // NOTE (Gabe 2024-09-19): If it's just a warning, we'll display it as a
      // toast and allow the process to continue.
      if (event.event === "warning") {
        const userFacingDetailsResult = errorUserFacingDetailsSchema.safeParse(
          JSON.parse(event.data),
        );
        if (
          userFacingDetailsResult.success &&
          userFacingDetailsResult.data.type === "toast" &&
          typeof userFacingDetailsResult.data.message === "string" &&
          typeof userFacingDetailsResult.data.detail === "string"
        ) {
          warningToast(
            userFacingDetailsResult.data.message,
            userFacingDetailsResult.data.detail,
          );
        }
        return;
      }

      if (event.event === "meta") {
        const parseResult = aiMetaEventSchema.safeParse(JSON.parse(event.data));
        if (parseResult.success) {
          metaEvents.push(parseResult.data);
        }
        return;
      }

      if (event.data === "") {
        return;
      }

      // Note (Evan, 2024-06-04): This type assertion is explicitly recommended
      // by the RTK docs, https://redux-toolkit.js.org/rtk-query/usage-with-typescript#typing-dispatch-and-getstate
      const state = getState() as EditorRootState;

      const parsedData = JSON.parse(event.data);

      const {
        data: action,
        thoughts,
        reasoning,
        component,
        chatId,
      } = parsedData;

      // We also want to bring the thoughts into the chatbot. Thoughts are currently handled in a separate event from actions
      // TODO (Yuxin, 2025-03-03): This code is completely fucked
      if (thoughts || reasoning) {
        dispatch(
          handleChatbotThoughts({
            thoughts,
            reasoning,
            chatId,
          }),
        );
      } else if (component) {
        dispatch(
          handleChatbotComponent({
            component,
          }),
        );
      } else if (action) {
        // Store the action in our actions array
        dispatch(
          handleAIActions({
            action,
          }),
        );

        dispatch(
          handleStreamingAction({
            ...action,
            activeCanvas: state.canvas.activeCanvas,
          }),
        );
        if (shouldScrollToComponent(action, metaEvents)) {
          dispatch(scrollToComponentId({ componentId: action.componentId }));
        }
      }
    },
    onerror: (error) => {
      // Note (Evan, 2024-06-10): Return to normal editing
      dispatch(setStreamingUpdate(null));
      dispatch(setEditorMode("edit"));
      dispatch(setCanvasInteractionMode("edit"));

      // Note (Evan, 2024-06-10): We re-throw this so the error toast shows
      throw error;
    },
    onclose: () => {
      dispatch(markStreamingUpdateFinished());
      dispatch(setCanvasInteractionMode("edit"));

      signal.removeEventListener("abort", abortHandler);
      onFinish?.();
    },
  });
};

export const aiApi = createApi({
  reducerPath: "aiApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${getPublisherUrl()}/api/v1/ai`,
    credentials: defaultFetchCredentials,
    prepareHeaders: (headers) => {
      headers.set("Content-Type", "application/json");
      const token = getTokenFromStorage();

      if (token) {
        headers.set("Authorization", `Token ${token}`);
      }

      return headers;
    },
  }),
  endpoints: (builder) => ({
    aiTemplate: builder.mutation<
      {},
      Omit<BuildAssistantParams, KeysPopulatedInQueryFn> & {
        selectedProduct?: SchemaReploShopifyProduct | null;
        projectId: string;
        triggeringFeature?: AITriggeringFeature;
        onFinish?: () => void;
      }
    >({
      queryFn: async (
        {
          componentsToGenerate,
          onFinish,
          projectId,
          systemPrompt,
          initialPrompt,
          selectedProduct,
          triggeringFeature,
          useChainOfThought,
          contextComponent,
          contextComponentMapping,
        },
        api,
      ) => {
        const body: Omit<BuildAssistantParams, KeysPopulatedInQueryFn> = {
          componentsToGenerate,
          projectId,
          systemPrompt,
          initialPrompt,
          selectedProduct,
          triggeringFeature,
          useChainOfThought,
          contextComponent,
          contextComponentMapping,
        };

        if (!componentsToGenerate.length) {
          return { data: {} };
        }

        try {
          // Note (Evan, 2024-07-18): IMPORTANT: this endpoint is currently rate-limited to a maximum of
          // 1 action per 10ms, on the backend (routes/buildAssistant.ts) - this is in order to allow the editor sufficient time
          // to process each action. Though currently this approach doesn't seem to add net time to text generation,
          // that could become the case in the future, particularly if the behavior of our LLM provider (currently OpenAI)
          // changes. So if things become slow in the future, that is the place to start.
          await aiStreamingQuery(
            {
              url: `${getPublisherUrl()}/api/v2/ai/build-assistant`,
              body,
              componentId: componentsToGenerate[0]!.component.id,
              shouldScrollToComponent: (_action, _metaEvents) => {
                return false;
              },
              onFinish,
            },
            api,
          );
          return { data: {} };
        } catch (error) {
          console.error(error);
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: String(error),
              data: error,
            },
          };
        }
      },
    }),
  }),
});

export const { useAiTemplateMutation } = aiApi;

const { reducer, actions } = aiSlice;
export const {
  handleChatbotThoughts,
  handleChatbotComponent,
  handleAIActions,
  resetAIActions,
} = actions;
export default reducer;
